<script>
import { toMoney, getNumber } from '@utils/Format'
import { customSort as baseCustomSort } from '@utils/Sort'
import { GlobalBus } from '@services/bus'
import { MODAL } from '@constants/Text'
import ClientTags from '@modules/crm/components/client-tags/client-tags.vue'
import CommentDisplay from '@modules/portfolio-review/components/comment-display/comment-display.vue'
import TableRowActions from '@modules/consolidator/components/table-row-actions/table-row-actions.vue'
import { RECOMENDACAO_COLORS, RECOMENDACAO_LABELS } from '@modules/consolidator/constants/recomendacao'

const FIX_DISABLED_TOOLTIP = 'Apenas operações de resgate podem ser fixadas'

export default {
  name: 'SimulatorOperationsTable',
  components: {
    ClientTags,
    CommentDisplay,
    TableRowActions,
  },
  props: {
    headers: {
      type: Array,
      default: () => [],
    },
    items: {
      type: Array,
      default: () => [],
    },
    groupBy: {
      type: [String, Array],
      default: null,
    },
    fixedAssets: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      recomendacaoColors: RECOMENDACAO_COLORS,
      recomendacaoLabels: RECOMENDACAO_LABELS,
    }
  },
  computed: {
    hasActionColumn() {
      const headerList = Array.isArray(this.headers) ? this.headers : []
      return headerList.some((headerItem) => {
        return headerItem && headerItem.value === 'actions'
      })
    },
    tableRows() {
      const itemList = Array.isArray(this.items) ? this.items : []
      return itemList.map((operationItem) => ({
        ...operationItem,
        actions: this.buildRowActions(operationItem),
      }))
    },
  },
  methods: {
    customSort(itemList, sortKey, sortDescList) {
      const sortDict = {
        texts: ['nome_ativo', 'operacao'],
        numbers: ['pl_atual', 'pl_final', 'movimentacao'],
        dates: [],
      }
      return baseCustomSort(itemList, sortKey, sortDescList, sortDict)
    },
    sumGroupMoney(groupItems, valueKey) {
      const safeItems = Array.isArray(groupItems) ? groupItems : []
      const totalValue = safeItems.reduce((currentTotal, assetItem) => {
        const rawValue = assetItem && assetItem[valueKey]
        return currentTotal + getNumber(rawValue)
      }, 0)
      return toMoney(totalValue)
    },
    getMovementClass(operationItem) {
      const operationType = operationItem && operationItem.operacao
      if (operationType === 'C') {
        return 'text-success'
      }
      if (operationType === 'V') {
        return 'text-danger'
      }
      return ''
    },
    getTags(assetItem) {
      if (!assetItem || !Array.isArray(assetItem.tags)) {
        return []
      }
      const rawTags = assetItem.tags
      return rawTags.filter((tagValue) => {
        return typeof tagValue === 'string' && tagValue.trim()
      })
    },
    emitEditNotes(assetItem) {
      GlobalBus.$emit(MODAL.PORTFOLIO_REVIEW_EDIT_NOTES, {
        asset: assetItem,
        onSave: this.handleSaveNotes,
      })
    },
    handleSaveNotes({ asset, comentario, setor, recomendacao, tags } = {}) {
      if (!asset) {
        return
      }
      const payload = this.buildNotesPayload({ asset, comentario, setor, recomendacao, tags })
      this.$emit('save-notes', payload)
    },
    buildNotesPayload({ asset, comentario, setor, recomendacao, tags } = {}) {
      const commentValue = typeof comentario === 'string' ? comentario : ''
      const setorValue = setor !== undefined ? setor : asset && asset.setor
      const tagsList = Array.isArray(tags) ? tags : []
      return {
        asset,
        comentario: commentValue,
        setor: setorValue || '',
        recomendacao: recomendacao !== undefined ? recomendacao : (asset && asset.recomendacao) || null,
        tags: tagsList,
      }
    },
    emitFixAsset(assetItem) {
      this.$emit('fix-asset', assetItem)
    },
    isAssetFixed(assetItem) {
      if (!assetItem || !Array.isArray(this.fixedAssets)) {
        return false
      }
      return this.fixedAssets.some((fixedItem) => {
        return fixedItem && fixedItem.nome_ativo === assetItem.nome_ativo
      })
    },
    buildRowActions(operationItem) {
      const isFixed = this.isAssetFixed(operationItem)
      const canFix = !!(operationItem && operationItem.operacao === 'V')
      const fixLabel = isFixed ? 'Remover ativo dos fixados' : 'Fixar ativo'
      const fixTooltip = canFix ? fixLabel : FIX_DISABLED_TOOLTIP
      return [
        {
          key: 'edit-notes',
          icon: 'mdi-note-edit-outline',
          tooltip: 'Editar comentário e tags',
          handler: () => this.emitEditNotes(operationItem),
        },
        {
          key: 'fix-asset',
          icon: isFixed ? 'mdi-pin-off-outline' : 'mdi-pin-outline',
          tooltip: fixTooltip,
          disabled: !canFix,
          handler: () => this.emitFixAsset(operationItem),
        },
      ]
    },
  },
}
</script>

<template>
  <div>
    <v-data-table
      :headers="headers"
      :items="tableRows"
      :group-by="groupBy"
      :custom-sort="customSort"
      class="table-new-ux-group-by-v1"
      hide-default-footer
      disable-pagination
    >
      <template v-slot:group.header="{ group, items: groupItems }">
        <td
          v-for="headerItem in headers"
          :key="headerItem.value || headerItem.text"
          class="text-semibold"
        >
          <span v-if="headerItem.value === 'nome_ativo'">
            {{ group }}
          </span>
          <span v-else-if="headerItem.value === 'pl_atual'">
            {{ sumGroupMoney(groupItems, 'pl_atual') }}
          </span>
          <span v-else-if="headerItem.value === 'pl_final'">
            {{ sumGroupMoney(groupItems, 'pl_final') }}
          </span>
        </td>
      </template>

      <template v-slot:item.movimentacao="{ item }">
        <div :class="getMovementClass(item)">
          {{ item.movimentacao }}
        </div>
      </template>

      <template v-slot:item.recomendacao="{ item }">
        <v-chip
          v-if="item.recomendacao"
          x-small
          :color="recomendacaoColors[item.recomendacao] || 'grey'"
          dark
        >
          {{ recomendacaoLabels[item.recomendacao] || item.recomendacao }}
        </v-chip>
        <span v-else> - </span>
      </template>

      <template v-slot:item.setor="{ item }">
        <v-tooltip v-if="item.setor" top>
          <template v-slot:activator="{ on, attrs }">
            <div class="text-truncate" style="max-width: 120px" v-bind="attrs" v-on="on">
              {{ item.setor }}
            </div>
          </template>
          <span>{{ item.setor }}</span>
        </v-tooltip>
        <div v-else> - </div>
      </template>

      <template v-slot:item.comentario="{ item }">
        <CommentDisplay
          :comment-text="item && item.comentario"
          display-mode="text"
        />
      </template>

      <template v-slot:item.tags="{ item }">
        <ClientTags
          v-if="getTags(item).length"
          :tags="getTags(item)"
          useSmallChips
        />
        <div v-else> - </div>
      </template>

      <template v-slot:item.actions="{ item }">
        <TableRowActions v-if="hasActionColumn" :actions="item.actions" />
      </template>
    </v-data-table>
  </div>
</template>

